import { inject, Injectable } from '@angular/core';
import { PlatformLocation } from '@angular/common';

const STORAGE_KEY = 'mifl_platform_login_active';
const LOGIN_CALLBACK_PATH = '/login-callback';

/** Token-refresh appId for platform login sessions. */
export const PLATFORM_LOGIN_APP_ID = 'mifl';
/** Token-refresh appId for legacy (midc redirect) sessions. */
export const LEGACY_APP_ID = 'midc';

/**
 * Tracks whether the current session came through the new iTero Platform Login flow.
 * Guards use it to skip legacy checks; the appId provider uses it to pick the correct
 * token-refresh appId ('mifl' vs 'midc').
 *
 * NOTE: Uses localStorage instead of sessionStorage so the state survives browser close/reopen.
 * Chrome clears sessionStorage on browser close (per HTML5 spec), while Firefox/Safari may restore it.
 * The logout flow calls localStorage.clear() anyway, so cleanup is still handled properly.
 */
@Injectable({
	providedIn: 'root',
})
export class PlatformLoginStateService {
	private readonly platformLocation = inject(PlatformLocation);
	private active = false;

	constructor() {
		// The app loads at /login-callback only for the platform login flow. Detect once
		// and persist, so state survives navigation and reloads.
		if (this.platformLocation.pathname?.startsWith(LOGIN_CALLBACK_PATH)) {
			this.activate();
		} else {
			this.active = localStorage.getItem(STORAGE_KEY) === 'true';
		}
	}

	/** Mark the platform login flow as active for this session. */
	activate(): void {
		this.active = true;
		localStorage.setItem(STORAGE_KEY, 'true');
	}

	/** Returns true if the current session used the new platform login flow. */
	isActive(): boolean {
		return this.active;
	}

	/** The appId auth-angular must use to refresh tokens for the current session's flow. */
	getRefreshAppId(): string {
		return this.active ? PLATFORM_LOGIN_APP_ID : LEGACY_APP_ID;
	}

	/** Clear the platform login state (e.g., on logout). */
	clear(): void {
		this.active = false;
		localStorage.removeItem(STORAGE_KEY);
	}
}
